import { Component, input, signal, computed, OnDestroy, inject } from '@angular/core';
import { StatusSidebar } from '@app/shared/components/status-sidebar/status-sidebar';
import { ProjectsService, TaskService } from '../../projects.service';
import { CrossTabSyncService } from '@app/core/services/cross-tab-sync/cross-tab-sync.service';
import { Subject, takeUntil, filter } from 'rxjs';

@Component({
  selector: 'app-task-status',
  standalone: true,
  imports: [StatusSidebar],
  templateUrl: './task-status.html',
  styleUrl: './task-status.scss',
})
export class TaskStatus implements OnDestroy {
  taskId = input<string | null>(null);
  refreshTrigger = input<number>(0);
  
  // Internal refresh trigger signal for cross-tab sync
  private internalRefreshTrigger = signal<number>(0);
  private readonly destroy$ = new Subject<void>();
  private readonly crossTabSyncService = inject(CrossTabSyncService);
  
  // Computed signal that combines input and internal refresh triggers
  effectiveRefreshTrigger = computed(() => {
    return this.refreshTrigger() + this.internalRefreshTrigger();
  });

  // Configuration for issue status
  taskConfig = {
    type: 'task' as const,
    title: 'Manage Task',
    statusLabel: 'Status',
    assignLabel: 'Assigned To',
    plannedHoursLabel: 'Planned Hrs',
    dueDateLabel: 'Due on',
    refNumberLabel: 'Ref No.',
    showPlannedHours: true,
    showDueDate: true,
    showRefNumber: true,
    showApprovals: true,
    showTimeSummary: true,
    customFields: [],
  };

  constructor(
    public taskService: TaskService,
    public projectsService: ProjectsService
  ) {
    this.setupCrossTabSync();
  }

  /**
   * Setup cross-tab synchronization for task status updates
   */
  private setupCrossTabSync(): void {
    this.crossTabSyncService.getSyncEvents()
      .pipe(
        takeUntil(this.destroy$),
        filter((event): event is any => !!event && !!event.type)
      )
      .subscribe((event) => {
        const currentTaskId = this.taskId();
        if (!currentTaskId) return;

        const current = String(currentTaskId);
        const candidateIds = [
          event.taskId,
          event.data?.taskId,
          event.data?.task?._id,
          event.data?.task?.id,
        ].filter((v) => v != null && v !== '');
        const matches = candidateIds.some((v) => String(v) === current);
        if (!matches) return;

        // Match task-list / task-details: field edits use task_field_updated; status ng-select uses
        // task_status_updated; task-list legacy/localStorage used task_updated without CrossTabSync — now emitted too.
        const refreshTypes = [
          'task_status_updated',
          'task_field_updated',
          'task_updated',
        ];
        if (refreshTypes.includes(event.type)) {
          this.internalRefreshTrigger.update((value) => value + 1);
        }
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
